/**
 * The Issuance Tool's SSL Certificate archive : the filenames inside it
 * and the README that explains them.
 *
 * MODELLED ON tracking-01's `lib/zip-generator.ts`, deliberately, after
 * the operator compared the two archives on 2026-08-23 : "the naming
 * shouldn't differ too much and I believe we should be doing things the
 * same way rather than differently". That file is the reference ; this
 * one follows its prefix shape, its file-naming dictionary and its
 * README structure. Where the two diverge it is because the ARCHIVES
 * differ, never because the convention does :
 *
 *   - No PKCS#7 (.p7b). This tool does not produce one, and the
 *     operator ruled it out of scope on 2026-08-23.
 *   - No root-bearing fullchain. RFC 8555 §7.4.2 says the Certificate
 *     Authority SHOULD NOT send the trust anchor, so what arrives here
 *     is the leaf and the intermediates and nothing else. Tracking's
 *     source data carries a root ; ours does not, so the file tracking
 *     calls `-fullchain.pem` cannot exist here and is not invented.
 *   - A Private Key section, which tracking's archive never contains.
 *     It is the only file in here that cannot be re-fetched from the
 *     Certificate Authority, so it carries the longest section.
 *
 * WHY THE DICTIONARY MATTERS. Before this module the four files were
 * named one slot too inclusive : the intermediates-only file was called
 * `chain`, and the leaf-plus-intermediates file was called `fullchain`.
 * A customer who had read tracking's README and then downloaded from
 * this tool would take `fullchain` to include the Root SSL Certificate.
 * It never did. The names below are tracking's, meaning what tracking
 * means by them.
 */

/**
 * The four members of the archive, keyed by the artifact they carry.
 *
 * `type` is the word that lands in the filename and `ext` its
 * extension, both matching tracking's dictionary : the leaf is a `.crt`
 * there, the chain files are `.pem`. Held as ONE table because the ZIP
 * and each artifact card's own Download button both read it, and the
 * two must never disagree about what a file is called.
 */
export const ARCHIVE_MEMBERS = {
  leaf: { type: "certificate", ext: "crt" },
  intermediates: { type: "intermediates", ext: "pem" },
  chain: { type: "chain", ext: "pem" },
  privateKey: { type: "privatekey", ext: "pem" },
};

/**
 * Build the archive's filename prefix.
 *
 * Shape : `{identifier}-{YYYYMMDD-HHMMSS}`, which is tracking's
 * `generateFilenamePrefix` with the domain standing in for the order
 * number this tool has no equivalent of. The timestamp sits BEFORE the
 * file type, unlike the rest of this site's downloads, so an archive
 * from either system sorts and reads the same way.
 *
 * CALL THIS ONCE PER ARCHIVE. Every filename must carry the same
 * instant : the previous helper stamped each file as it was built, so a
 * second ticking over mid-archive produced members whose timestamps
 * disagreed. Tracking's own JSDoc gives the same instruction for the
 * same reason.
 *
 * @param {string} sanitizedIdentifier - an ALREADY-sanitised domain
 * @param {string} timestamp - one `generateTimestamp()` result
 */
export function archivePrefix(sanitizedIdentifier, timestamp) {
  return `${sanitizedIdentifier}-${timestamp}`;
}

/**
 * The filename for one member of the archive.
 *
 * @param {string} prefix - from `archivePrefix`, shared by every member
 * @param {{type: string, ext: string}} member - an ARCHIVE_MEMBERS entry
 */
export function archiveFilename(prefix, member) {
  return `${prefix}-${member.type}.${member.ext}`;
}

/**
 * The README carried in every SSL Certificate archive.
 *
 * The heading rule, the register and the section order are tracking's.
 * `{prefix}` is replaced at build time so the document names the exact
 * files sitting beside it rather than describing a pattern.
 */
export const ARCHIVE_README_CONTENT = `=============================================
Trustico® SSL Certificate Archive
=============================================

This ZIP archive contains your SSL Certificate in multiple formats,
along with the private key it was issued against. Your archive may
contain some or all of the following files depending on your SSL
Certificate's chain of trust.


{prefix}-certificate.crt
---------------------------------------------

Your SSL Certificate (End-Entity SSL Certificate). This is the SSL
Certificate issued specifically for your domain. Most server
configurations require this file. On its own, this SSL Certificate
does not include the Intermediate SSL Certificates needed to establish
a complete chain of trust.


{prefix}-intermediates.pem
---------------------------------------------

The Intermediate SSL Certificate(s) only, without your SSL Certificate.
Use this file when your server configuration expects the SSL
Certificate and the chain to be supplied as separate files (for
example, Apache's SSLCertificateChainFile directive when paired with a
separate SSLCertificateFile). This file is only included when
Intermediate SSL Certificates are part of your SSL Certificate's chain
of trust.


{prefix}-chain.pem
---------------------------------------------

Your SSL Certificate combined with the Intermediate SSL Certificate(s)
that link it to the trusted Root Certificate Authority (CA). This file
does not include the Root SSL Certificate itself, as most clients and
browsers already have Root SSL Certificates pre-installed. Use this
file when your server expects the SSL Certificate and chain in a single
file (for example, Nginx's ssl_certificate directive). If your server
expects the SSL Certificate and chain in separate files, use the
intermediates-only file above instead.


{prefix}-privatekey.pem
---------------------------------------------

The private key generated for your SSL Certificate. Install it on the
same server as your SSL Certificate, as the two only work as a matched
pair and an SSL Certificate cannot be used without it. Keep this file
secure and never send it to anybody. It is destroyed along with the
issuance request and cannot be reissued, so this archive and the
Trustico® configuration file are the only copies you will have.
`;

/**
 * The README with every `{prefix}` placeholder filled in.
 *
 * @param {string} prefix - from `archivePrefix`
 */
export function archiveReadme(prefix) {
  return ARCHIVE_README_CONTENT.replace(/\{prefix\}/g, prefix);
}
